import * as React from 'react';
import PageEventContext from './context';
import type {
  FlowEventContextValue,
  FlowEventTracker,
  InternalPageFlowEventContextValue,
  PageFlowEventTracker,
} from './types';
import logInNonLive from '@tob/shared/utils/log-in-nonlive';
import { TrackingContext } from '@shopee-user/tracking';

// Page Level
export function usePageEventContext<EventDefinition>() {
  return React.useContext(
    PageEventContext
  ) as FlowEventContextValue<EventDefinition>;
}

function useInternalPageEventContext<EventDefinition>() {
  const pageEventContext = React.useContext(
    PageEventContext
  ) as InternalPageFlowEventContextValue<EventDefinition>;
  if (pageEventContext.trackSubTrackerEvent === null || pageEventContext.trackSubTrackerEvent === undefined) {
    // parent page doesn't wrap by context provider
    return null;
  }
  return pageEventContext;
}

export function withPageEvent<
  EventDefinition,
  FlowEventState extends object = {},
  Props extends object = {}
>(
  state: FlowEventState,
  tracker: PageFlowEventTracker<EventDefinition, FlowEventState>
) {
  const initialState = { ...state };

  return (Component: React.ComponentType<Props>) => {
    const RootEventContext = PageEventContext as any as React.Context<
      InternalPageFlowEventContextValue<EventDefinition>
    >;

    const WrapperComponent = (props: Props) => {
      const flowEventState = React.useRef(initialState);
      const { sendTrackingEvent, sendImpressionEvent } = React.useContext(TrackingContext);

      const trackEvent = <EventName extends keyof EventDefinition>(
        name: EventName,
        ...args: undefined extends EventDefinition[EventName]
          // eslint-disable-next-line prettier/prettier
          ? [details?: EventDefinition[EventName]]
          : [details: EventDefinition[EventName]]
      ) => {
        const handler = tracker.handlers[name];
        if (handler) {
          logInNonLive(
            `[${tracker.name}] track event --> ${String(name)}     args:`,
            ...args
          );
          const ret = handler.apply(tracker, args);
          if (ret === null || ret === undefined) {
            // no state change
            // nothing need to do
          } else if (typeof ret === 'function') {
            // state change
            // function type is returned, similar with:
            /**
             * const [state, setState] = React.useState({});
             * setState((state) => ({...state, newKey: 'newValue'}));
             */
            const nestedRet = ret({
              state: Object.freeze({ ...flowEventState.current }),
              tools: { sendBITrackingEvent: sendTrackingEvent, sendImpressionEvent },
            });

            if (nestedRet && typeof nestedRet === 'object') {
              flowEventState.current = {
                ...flowEventState.current,
                ...nestedRet,
              };
            }
          } else {
            // state change
            // object instance is returned, similar with:
            /**
             * const [state, setState] = React.useState({});
             * setState({...state, newKey: 'newValue'});
             */
            flowEventState.current = { ...flowEventState.current, ...ret };
          }
          return true;
        } else {
          return false;
        }
      };

      const trackSubTrackerEvent = <
        ComponentEventDefinition,
        ComponentFlowEventState extends object = {}
      >({
        subTrackerName,
        eventName,
        getState,
        updateState,
        args,
      }: {
        subTrackerName: string;
        eventName: keyof ComponentEventDefinition;
        args: undefined extends ComponentEventDefinition[keyof ComponentEventDefinition]
          ? [details?: ComponentEventDefinition[keyof ComponentEventDefinition]]
          : [details: ComponentEventDefinition[keyof ComponentEventDefinition]];
        getState: () => ComponentFlowEventState;
        updateState: (state: Partial<ComponentFlowEventState>) => void;
      }) => {
        const subTracker = tracker.subTrackers
          ? tracker.subTrackers[subTrackerName]
          : undefined;
        const overridedHandler = subTracker?.handlers[eventName];
        if (overridedHandler) {
          logInNonLive(
            `[${tracker.name}][${subTrackerName}] track event --> ${String(
              eventName
            )}     args:`,
            ...args
          );
          const ret = overridedHandler.apply(tracker, args);
          if (ret === null || ret === undefined) {
            // no state change
            // nothing need to do
          } else if (typeof ret === 'function') {
            // state change
            const currentSubState = getState();
            const nestedRet = ret({
              state: currentSubState,
              pageState: Object.freeze({ ...flowEventState.current }),
              tools: { sendBITrackingEvent: sendTrackingEvent, sendImpressionEvent },
            });
            if (nestedRet && typeof nestedRet === 'object') {
              updateState({ ...currentSubState, ...nestedRet });
            }
          } else {
            // state change
            updateState({ ...getState(), ...ret });
          }
          return true;
        }
        return false;
      };

      return (
        <RootEventContext.Provider
          value={
            {
              trackEvent,
              trackSubTrackerEvent, // this function is be protected
            } as any
          }
        >
          <Component {...props} />
        </RootEventContext.Provider>
      );
    };

    WrapperComponent.displayName = `withPageEvent(${Component.displayName || Component.name || 'Component'})`;
    return WrapperComponent;
  };
}

// Component Level
export function createComponentEventContext<
  EventDefinition,
  FlowEventState extends object = {}
>(state: FlowEventState, tracker: FlowEventTracker<EventDefinition>) {
  const initialState = { ...state };

  const ComponentEventContext = React.createContext<
    FlowEventContextValue<EventDefinition>
  >({
    trackEvent: () => false,
  });

  const useComponentEventContext = () => {
    return React.useContext(ComponentEventContext);
  };

  const withComponentEvent = <Props extends object = {}>(
    Component: React.ComponentType<Props>
  ) => {
    const WrapperComponent = (props: Props) => {
      const flowEventState = React.useRef(initialState);
      const { sendTrackingEvent, sendImpressionEvent } = React.useContext(TrackingContext);
      // why we use Any generic type here?
      // because component doesn't know who is the parent/page.
      // A shared component could be used for multiple biz pages.
      const pageEventContext = useInternalPageEventContext<any>();

      const trackEvent = <EventName extends keyof EventDefinition>(
        name: EventName,
        ...args: undefined extends EventDefinition[EventName]
          ? [details?: EventDefinition[EventName]]
          : [details: EventDefinition[EventName]]
      ) => {
        if (
          pageEventContext &&
          pageEventContext.trackSubTrackerEvent({
            subTrackerName: tracker.name,
            eventName: name,
            args,
            getState: () => Object.freeze({ ...flowEventState.current }),
            updateState: (upserts: Partial<FlowEventState>) => {
              flowEventState.current = {
                ...flowEventState.current,
                ...upserts,
              };
            },
          })
        ) {
          return true;
        }
        const handler = tracker.handlers[name];
        if (handler) {
          logInNonLive(
            `[${tracker.name}] track event --> ${String(name)}     args:`,
            ...args
          );
          const ret = handler.apply(tracker, args);
          if (ret === null || ret === undefined) {
            // no state change
            // nothing need to do
          } else if (typeof ret === 'function') {
            // state change
            const nestedRet = ret({
              state: Object.freeze({ ...flowEventState.current }),
              tools: { sendBITrackingEvent: sendTrackingEvent , sendImpressionEvent},
            });
            if (nestedRet && typeof nestedRet === 'object') {
              flowEventState.current = {
                ...flowEventState.current,
                ...nestedRet,
              };
            }
          } else {
            // state change
            flowEventState.current = { ...flowEventState.current, ...ret };
          }
          return true;
        } else {
          return false;
        }
      };

      return (
        <ComponentEventContext.Provider value={{ trackEvent }}>
          <Component {...props} />
        </ComponentEventContext.Provider>
      );
    };

    WrapperComponent.displayName = `withComponentEvent(${Component.displayName || Component.name || 'Component'})`;
    return WrapperComponent;
  };

  return {
    ComponentEventContext,
    useComponentEventContext,
    withComponentEvent,
  };
}
